import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, DollarSign, TrendingUp, MousePointerClick, Loader2, Trash2, Trophy, Building2, Wallet, CheckCircle2, Copy } from 'lucide-react';
import { toast } from 'sonner';

const formatAmount = (amount, currency) => {
  try { return new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(amount); }
  catch { return `${amount} ${currency}`; }
};

const StatusPill = ({ s }) => {
  const map = {
    pending: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    accepted: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
    rejected: 'bg-rose-500/20 text-rose-300 border-rose-500/30',
  };
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${map[s] || 'bg-white/10'}`}>{s}</span>;
};

export default function AdminMonetizationModal({ onClose }) {
  const [data, setData] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [pendingPayouts, setPendingPayouts] = useState({ summary: [], rows: [], pending_count: 0 });
  const [filter, setFilter] = useState('accepted'); // accepted | pending | rejected | all
  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState(null);

  const reload = async (status = filter) => {
    setLoading(true);
    try {
      const [r, lb, pp] = await Promise.all([
        axios.get('/api/monetization/admin/overview', { params: { status } }),
        axios.get('/api/monetization/admin/leaderboard', { params: { limit: 20 } }).catch(() => ({ data: { leaderboard: [] } })),
        axios.get('/api/monetization/admin/payouts-pending').catch(() => ({ data: { summary: [], rows: [], pending_count: 0 } })),
      ]);
      setData(r.data);
      setLeaderboard(lb.data?.leaderboard || []);
      setPendingPayouts(pp.data || { summary: [], rows: [], pending_count: 0 });
    } catch (_e) { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { reload(filter); /* eslint-disable-next-line */ }, [filter]);

  const markBulkPaid = async (creatorId, currency) => {
    if (!window.confirm('Marquer tous les paiements de ce créateur (' + currency + ') comme reversés ?')) return;
    setPayingId(`${creatorId}-${currency}`);
    try {
      const r = await axios.post('/api/monetization/admin/payouts/mark-bulk-paid', { creator_id: creatorId, currency });
      toast.success(`${r.data.updated} reversement(s) marqué(s) comme payés`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setPayingId(null); }
  };

  const copyIban = (iban) => {
    try {
      navigator.clipboard.writeText(iban || '');
      toast.success('IBAN copié');
    } catch { toast.error('Impossible de copier'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer ce contrat de l\'historique ? Les clics associés seront aussi supprimés.')) return;
    try {
      await axios.delete(`/api/monetization/admin/requests/${id}`);
      toast.success('Supprimé');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-md flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="w-full max-w-2xl max-h-[92vh] overflow-y-auto bg-gray-900 text-white rounded-t-3xl sm:rounded-3xl border border-white/10">
        <div className="sticky top-0 bg-gray-900/95 backdrop-blur-md border-b border-white/10 px-5 py-3 flex items-center justify-between">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-emerald-400" /> Monétisation créateurs
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/10" data-testid="admin-monetization-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {loading && <div className="flex items-center justify-center py-10 text-gray-400"><Loader2 className="w-5 h-5 animate-spin" /></div>}

          {!loading && data && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <SummaryCard icon={TrendingUp} label="En attente" value={data.summary.pending} color="amber" />
                <SummaryCard icon={TrendingUp} label="Acceptés" value={data.summary.accepted} color="emerald" />
                <SummaryCard icon={TrendingUp} label="Refusés" value={data.summary.rejected} color="rose" />
                <SummaryCard icon={MousePointerClick} label="Clics totaux" value={data.summary.total_clicks} color="cyan" />
              </div>

              {data.by_currency.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2 text-sm text-gray-400 uppercase tracking-wider">Volume par devise (acceptés)</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {data.by_currency.map((c) => (
                      <div key={c.currency} className="p-3 bg-white/5 rounded-xl border border-white/10">
                        <div className="text-xs text-gray-400">{c.currency} · {c.count} contrats</div>
                        <div className="text-lg font-bold tabular-nums">{formatAmount(c.total, c.currency)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Reversements à effectuer — Section comptable */}
              <div data-testid="admin-payouts-pending-section" className="p-3 rounded-2xl bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/30">
                <h3 className="font-semibold mb-3 text-sm text-amber-200 uppercase tracking-wider flex items-center gap-1.5">
                  <Wallet className="w-4 h-4 text-amber-300" /> Reversements à effectuer ({pendingPayouts.pending_count})
                </h3>
                {pendingPayouts.summary.length === 0 ? (
                  <p className="text-xs text-gray-400 py-2">Aucun reversement en attente. Tous les créateurs sont à jour ✅</p>
                ) : (
                  <>
                    <p className="text-[11px] text-amber-100/80 mb-2">
                      Le comptable vire les montants ci-dessous par SEPA sur l'IBAN de chaque créateur, puis clique « Marquer payé ».
                    </p>
                    <div className="space-y-2">
                      {pendingPayouts.summary.map((s) => (
                        <div key={`${s.creator_id}-${s.currency}`} className="p-3 bg-black/30 rounded-xl border border-amber-500/20" data-testid={`payout-row-${s.creator_id}-${s.currency}`}>
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="min-w-0 flex-1">
                              <div className="font-semibold text-sm text-white truncate">{s.creator_name || s.creator_id}</div>
                              <div className="text-[11px] text-gray-400">{s.count} paiement{s.count > 1 ? 's' : ''} · {s.currency}</div>
                            </div>
                            <div className="text-base font-bold text-amber-200 tabular-nums shrink-0">{formatAmount(s.total, s.currency)}</div>
                          </div>
                          {s.iban ? (
                            <div className="p-2 bg-cyan-500/10 border border-cyan-500/30 rounded-lg mb-2">
                              <div className="flex items-center gap-1.5 text-[10px] text-cyan-200 uppercase tracking-wider mb-0.5">
                                <Building2 className="w-3 h-3" /> Coordonnées bancaires
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-xs break-all flex-1">{s.iban}</span>
                                <button onClick={() => copyIban(s.iban)} className="p-1 rounded hover:bg-white/10 shrink-0" data-testid={`copy-iban-${s.creator_id}`}><Copy className="w-3 h-3" /></button>
                              </div>
                              {s.bic && <div className="text-[10px] text-gray-400 mt-0.5">BIC : <span className="font-mono">{s.bic}</span></div>}
                              {s.holder_name && <div className="text-[10px] text-gray-400">Titulaire : {s.holder_name}</div>}
                            </div>
                          ) : (
                            <div className="p-2 bg-red-500/10 border border-red-500/30 rounded-lg mb-2 text-[11px] text-red-200">
                              ⚠️ Le créateur n'a pas encore saisi son IBAN. Le contacter avant de virer.
                            </div>
                          )}
                          <button
                            onClick={() => markBulkPaid(s.creator_id, s.currency)}
                            disabled={!s.iban || payingId === `${s.creator_id}-${s.currency}`}
                            className="w-full py-2 rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 text-xs font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
                            data-testid={`mark-paid-btn-${s.creator_id}-${s.currency}`}
                          >
                            {payingId === `${s.creator_id}-${s.currency}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                            Marquer payé — {formatAmount(s.total, s.currency)}
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Top creators leaderboard — 5 000+ abonnés triés par revenus */}
              <div data-testid="admin-monetization-leaderboard">
                <h3 className="font-semibold mb-2 text-sm text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                  <Trophy className="w-4 h-4 text-amber-300" /> Top créateurs (5 000+ abonnés)
                </h3>
                {leaderboard.length === 0 ? (
                  <p className="text-sm text-gray-500">Aucun paiement encore reçu.</p>
                ) : (
                  <div className="space-y-1.5">
                    {leaderboard.map((r, i) => (
                      <div key={`${r.creator_id}-${r.currency}`} className="flex items-center gap-2 p-2.5 bg-white/5 rounded-xl border border-white/10" data-testid={`leaderboard-row-${i}`}>
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs shrink-0 ${i === 0 ? 'bg-amber-400 text-black' : i === 1 ? 'bg-gray-300 text-black' : i === 2 ? 'bg-orange-400 text-black' : 'bg-white/10 text-white/70'}`}>
                          {i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{r.creator_name || r.creator_id}</div>
                          <div className="text-[11px] text-gray-400">{(r.followers || 0).toLocaleString('fr-FR')} abonnés · {r.sponsorship_count} contrats</div>
                        </div>
                        <div className="text-sm font-bold text-emerald-300 tabular-nums shrink-0">{formatAmount(r.total_earned, r.currency)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Filter chips */}
              <div className="flex flex-wrap gap-2">
                {[['accepted', 'Acceptés (historique)'], ['pending', 'En attente'], ['rejected', 'Refusés'], ['all', 'Tout']].map(([k, l]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setFilter(k)}
                    className={`px-3 py-1.5 rounded-full text-xs transition-colors ${filter === k ? 'bg-magenta-500 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}
                    data-testid={`admin-filter-${k}`}
                  >
                    {l}
                  </button>
                ))}
              </div>

              <div>
                <h3 className="font-semibold mb-2 text-sm text-gray-400 uppercase tracking-wider">Historique</h3>
                <div className="space-y-2">
                  {data.recent.map((r) => (
                    <div key={r.id} className="p-3 bg-white/5 rounded-xl border border-white/10 flex items-center justify-between gap-2" data-testid={`admin-recent-${r.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm truncate"><strong>{r.creator_name}</strong> → <span className="text-magenta-300">{r.sponsor_name}</span></div>
                        <div className="text-xs text-gray-400">{r.content_kind} · {formatAmount(r.amount, r.currency)} · {new Date(r.created_at).toLocaleDateString('fr-FR')}</div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <StatusPill s={r.status} />
                        <button
                          type="button"
                          onClick={() => remove(r.id)}
                          className="p-1.5 rounded-full hover:bg-rose-500/20 text-rose-300"
                          data-testid={`admin-delete-${r.id}`}
                          aria-label="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {data.recent.length === 0 && <p className="text-sm text-gray-500">Aucune entrée pour ce filtre.</p>}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, color }) {
  const map = {
    amber: 'from-amber-500 to-orange-500',
    emerald: 'from-emerald-500 to-green-500',
    rose: 'from-rose-500 to-red-500',
    cyan: 'from-cyan-500 to-blue-500',
  };
  return (
    <div className="bg-white/5 rounded-2xl p-3 border border-white/10 flex items-center gap-2">
      <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${map[color]} flex items-center justify-center`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-gray-400">{label}</div>
        <div className="text-lg font-bold tabular-nums">{(value ?? 0).toLocaleString('fr-FR')}</div>
      </div>
    </div>
  );
}
